import React, { useEffect, useState } from "react";
import OneSignal from "react-onesignal";
import { Bell, BellRing, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "./Atoms";

let _inited = false;

const PushOptIn = () => {
  const [ready, setReady] = useState(false);
  const [appId, setAppId] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState({ services: true, events: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/public-config");
        const id = r.data.onesignal_app_id;
        if (!id) { setReady(false); return; }
        setAppId(id);
        if (!_inited) {
          await OneSignal.init({ appId: id, allowLocalhostAsSecureOrigin: true, serviceWorkerParam: { scope: "/" }, notifyButton: { enable: false } });
          _inited = true;
        }
        if (!cancelled) {
          setReady(true);
          try {
            const isSub = await OneSignal.User.PushSubscription.optedIn;
            setSubscribed(!!isSub);
          } catch { /* optedIn read may fail on stale clients; non-fatal */ }
        }
      } catch {
        // OneSignal init may be blocked by browser policy — non-fatal
        setReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = async () => {
    if (!ready) { toast.error("Push notifications aren't configured yet."); return; }
    setBusy(true);
    try {
      await OneSignal.Slidedown.promptPush();
      // Wait briefly for the SDK to register
      await new Promise((res) => setTimeout(res, 800));
      try {
        await OneSignal.User.addTag("services", prefs.services ? "true" : "false");
        await OneSignal.User.addTag("events", prefs.events ? "true" : "false");
      } catch { /* tag write best-effort */ }
      try {
        const isSub = await OneSignal.User.PushSubscription.optedIn;
        setSubscribed(!!isSub);
        if (isSub) toast.success("Notifications enabled. You'll be the first to know.");
      } catch { /* optedIn read may fail on stale clients; non-fatal */ }
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("[push] enable failed", err);
      toast.error("Couldn't enable notifications. Check your browser permissions.");
    } finally { setBusy(false); }
  };

  if (!appId) return null; // hide entirely until configured

  return (
    <section id="notifications" className="relative py-24 px-6 section-ember">
      <div className="max-w-5xl mx-auto bento-card !p-8 sm:!p-12" data-testid="push-optin">
        <span className="glow-blob" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <SectionHeading
                overline="Service & Event Alerts"
                title={<>Don&rsquo;t miss the <span className="italic gold-text">fire signal</span></>}
              />
            </Reveal>
            <Reveal delay={80}>
              <p className="text-[#D9D9D9] text-[15px] leading-relaxed">
                Get an instant push notification on your phone or laptop when a service goes live,
                a prayer call starts, or a tent revival is announced.
              </p>
            </Reveal>
            <Reveal delay={160}>
              <div className="mt-5 space-y-2">
                <label className="check-wrap">
                  <input type="checkbox" checked={prefs.services} onChange={(e) => setPrefs({ ...prefs, services: e.target.checked })} data-testid="push-pref-services" />
                  Service start alerts (Sundays, revival nights, prayer calls)
                </label>
                <label className="check-wrap">
                  <input type="checkbox" checked={prefs.events} onChange={(e) => setPrefs({ ...prefs, events: e.target.checked })} data-testid="push-pref-events" />
                  Tent revivals, crusades & special events
                </label>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5 text-center md:text-right">
            <button onClick={enable} disabled={busy || subscribed} className="btn-fire w-full md:w-auto" data-testid="push-enable">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : subscribed ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
              {busy ? "Enabling…" : subscribed ? "Notifications On" : "Enable Notifications"}
            </button>
            <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-[#C9A44C]/70">
              Works on iOS, Android & desktop browsers
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PushOptIn;
